/**
 * Platform Admin Model Registry - Simplified Real API Version
 * 
 * Replaces mock data with real backend API integration.
 * Uses platformAdminModelRegistryService for all data operations.
 * 
 * Following BRAIN-PRE-CODE-CHECKLIST.md:
 * - Real API integration (no mock data)
 * - Defensive null checks
 * - Proper error handling
 * - Loading states
 */

import React, { useState, useEffect } from 'react';
import { RefreshCw, Trash2, Info, CloudUpload } from 'lucide-react';

import platformAdminModelRegistryService, { 
  ModelMetadata 
} from '../../../../services/platformAdminModelRegistryService';

const ModelRegistrySimplified: React.FC = () => {
  // State management
  const [models, setModels] = useState<ModelMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTenant, setSelectedTenant] = useState<string>('');
  const [deploymentStatusFilter, setDeploymentStatusFilter] = useState<string>('');
  const [includeDeprecated, setIncludeDeprecated] = useState(false);
  const [selectedModel, setSelectedModel] = useState<ModelMetadata | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [modelToDelete, setModelToDelete] = useState<{ modelId: string; tenantId: string; isProduction: boolean } | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Load models function - used by useEffect and manual refresh
  const loadModels = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await platformAdminModelRegistryService.listModels(
        selectedTenant || undefined,
        deploymentStatusFilter || undefined,
        includeDeprecated
      );
      
      setModels(response.models || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load models';
      const contextMessage = `Failed to load models${selectedTenant ? ` for tenant ${selectedTenant}` : ''}${deploymentStatusFilter ? ` with status ${deploymentStatusFilter}` : ''}`;
      setError(contextMessage + ': ' + errorMessage);
      setModels([]);
    } finally {
      setLoading(false);
    }
  };

  // Load models on mount and when filters change
  useEffect(() => {
    loadModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenant, deploymentStatusFilter, includeDeprecated]);

  const handleRefresh = () => {
    loadModels();
  };

  const handleViewDetails = async (model: ModelMetadata) => {
    try {
      setLoading(true);
      // Fetch full metadata
      const response = await platformAdminModelRegistryService.getModelMetadata(
        model.model_id,
        model.tenant_id
      );
      setSelectedModel(response.metadata);
      setDetailsDialogOpen(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to load details for model ${model.name} (${model.model_id}): ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (modelId: string, tenantId: string, newStatus: string) => {
    try {
      setLoading(true);
      await platformAdminModelRegistryService.updateDeploymentStatus(
        modelId,
        tenantId,
        newStatus
      );
      await loadModels(); // Refresh list
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to update deployment status to ${newStatus} for model ${modelId}: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteModel = (modelId: string, tenantId: string, isProduction: boolean) => {
    setModelToDelete({ modelId, tenantId, isProduction });
    setDeleteDialogOpen(true);
    setDeleteConfirmText('');
  };

  const confirmDelete = async () => {
    if (!modelToDelete || deleteConfirmText !== modelToDelete.modelId) {
      return;
    }

    try {
      setLoading(true);
      await platformAdminModelRegistryService.deleteModel(
        modelToDelete.modelId,
        modelToDelete.tenantId,
        modelToDelete.isProduction
      );
      await loadModels();
      setDeleteDialogOpen(false);
      setModelToDelete(null);
      setDeleteConfirmText('');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to delete model ${modelToDelete.modelId}: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setModelToDelete(null);
    setDeleteConfirmText('');
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'default' | 'error' | 'info' => {
    switch (status) {
      case 'deployed': return 'success';
      case 'deploying': return 'info';
      case 'pending': return 'default';
      case 'stopped': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const formatBytes = (bytes: number | null | undefined): string => {
    if (bytes === null || bytes === undefined) return 'N/A';
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const formatDate = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold text-white">Model Registry</h2>
          <p className="text-gray-400">Manage and monitor ML models across all tenants</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg transition-colors flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4">
          <div className="flex justify-between items-start">
            <p className="text-red-400">{error}</p>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
              ×
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-gray-800/50 rounded-lg p-4">
        <div className="flex gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm text-gray-400 mb-1">Tenant ID</label>
            <input
              type="text"
              value={selectedTenant}
              onChange={(e) => setSelectedTenant(e.target.value)}
              placeholder="Filter by tenant"
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
            />
          </div>
          
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm text-gray-400 mb-1">Deployment Status</label>
            <select
              value={deploymentStatusFilter}
              onChange={(e) => setDeploymentStatusFilter(e.target.value)}
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="deploying">Deploying</option>
              <option value="deployed">Deployed</option>
              <option value="stopped">Stopped</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm text-gray-400 mb-1">Include Deprecated</label>
            <select
              value={includeDeprecated ? 'yes' : 'no'}
              onChange={(e) => setIncludeDeprecated(e.target.value === 'yes')}
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
            >
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </div>
        </div>
      </div>

      {/* Models Table */}
      <div className="space-y-4">
        <div className="h-px bg-gradient-to-r from-blue-500/50 via-purple-500/50 to-blue-500/50" />
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-white">REGISTERED MODELS</h3>
        </div>
        <div className="h-px bg-gradient-to-r from-blue-500/50 via-purple-500/50 to-blue-500/50" />

        {loading ? (
          <div className="flex justify-center items-center p-8">
            <div className="text-gray-400">Loading models...</div>
          </div>
        ) : models.length === 0 ? (
          <div className="bg-gray-800/30 rounded-lg p-12 text-center border-2 border-dashed border-gray-700">
            <p className="text-gray-400 text-lg mb-2">No models found.</p>
            <p className="text-sm text-gray-500">
              {selectedTenant || deploymentStatusFilter ? 'Try adjusting filters.' : 'Register your first model to get started.'}
            </p>
          </div>
        ) : (
          <div className="bg-gray-800/50 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-900/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Model Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Version</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Tenant ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Size</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Access Count</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Created</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {models.map((model) => (
                    <tr key={model.model_id} className="hover:bg-gray-700/30 transition-colors">
                      <td className="px-4 py-4 text-sm text-white">{model.name}</td>
                      <td className="px-4 py-4 text-sm text-gray-300">v{model.version}</td>
                      <td className="px-4 py-4 text-sm text-gray-400 font-mono">
                        {model.tenant_id.substring(0, 8)}...
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-300">{model.model_type}</td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          model.deployment_status === 'deployed' ? 'bg-green-500/20 text-green-400' :
                          model.deployment_status === 'deploying' ? 'bg-blue-500/20 text-blue-400' :
                          model.deployment_status === 'pending' ? 'bg-gray-500/20 text-gray-400' :
                          model.deployment_status === 'stopped' ? 'bg-yellow-500/20 text-yellow-400' :
                          model.deployment_status === 'failed' ? 'bg-red-500/20 text-red-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {model.deployment_status}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-300">{formatBytes(model.size_bytes)}</td>
                      <td className="px-4 py-4 text-sm text-gray-300">{model.access_count ?? 0}</td>
                      <td className="px-4 py-4 text-sm text-gray-400">{formatDate(model.created_at)}</td>
                      <td className="px-4 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleViewDetails(model)}
                            className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded transition-colors"
                            title="View Details"
                          >
                            <Info className="w-4 h-4" />
                          </button>
                          
                          {model.deployment_status !== 'deployed' && (
                            <button
                              onClick={() => handleUpdateStatus(model.model_id, model.tenant_id, 'deployed')}
                              className="p-1.5 text-green-400 hover:text-green-300 hover:bg-green-500/10 rounded transition-colors"
                              title="Deploy Model"
                            >
                              <CloudUpload className="w-4 h-4" />
                            </button>
                          )}
                          
                          <button
                            onClick={() => handleDeleteModel(model.model_id, model.tenant_id, model.is_production)}
                            className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors"
                            title="Delete Model"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Model Details Modal */}
      {detailsDialogOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-900 p-6 rounded-2xl w-full max-w-2xl shadow-2xl border border-gray-700 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-white mb-4">Model Details</h2>
            
            {selectedModel && (
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-gray-400 mb-1">Model ID</div>
                  <div className="text-white font-mono text-sm">{selectedModel.model_id}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-400 mb-1">Name</div>
                  <div className="text-white">{selectedModel.name}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-400 mb-1">Description</div>
                  <div className="text-white">{selectedModel.description || 'No description'}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-400 mb-1">Tenant ID</div>
                  <div className="text-white font-mono text-sm">{selectedModel.tenant_id}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-400 mb-1">Model Type</div>
                  <div className="text-white">{selectedModel.model_type}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-400 mb-1">Deployment Status</div>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                    selectedModel.deployment_status === 'deployed' ? 'bg-green-500/20 text-green-400' :
                    selectedModel.deployment_status === 'deploying' ? 'bg-blue-500/20 text-blue-400' :
                    selectedModel.deployment_status === 'pending' ? 'bg-gray-500/20 text-gray-400' :
                    selectedModel.deployment_status === 'stopped' ? 'bg-yellow-500/20 text-yellow-400' :
                    selectedModel.deployment_status === 'failed' ? 'bg-red-500/20 text-red-400' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>
                    {selectedModel.deployment_status}
                  </span>
                </div>
                
                <div>
                  <div className="text-sm text-gray-400 mb-1">File Path</div>
                  <div className="text-white font-mono text-xs break-all">{selectedModel.path}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-400 mb-1">Size</div>
                  <div className="text-white">{formatBytes(selectedModel.size_bytes)}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-400 mb-1">Access Count</div>
                  <div className="text-white">{selectedModel.access_count ?? 0}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-400 mb-1">Last Accessed</div>
                  <div className="text-white">
                    {selectedModel.last_accessed ? formatDate(selectedModel.last_accessed) : 'Never'}
                  </div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-400 mb-1">Created</div>
                  <div className="text-white">{formatDate(selectedModel.created_at)}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-400 mb-1">Updated</div>
                  <div className="text-white">{formatDate(selectedModel.updated_at)}</div>
                </div>
                
                {selectedModel.tags && selectedModel.tags.length > 0 && (
                  <div>
                    <div className="text-sm text-gray-400 mb-2">Tags</div>
                    <div className="flex gap-2 flex-wrap">
                      {selectedModel.tags.map((tag, idx) => (
                        <span key={idx} className="bg-gray-700/50 text-gray-300 px-2 py-1 rounded text-xs">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedModel.metrics && Object.keys(selectedModel.metrics).length > 0 && (
                  <div>
                    <div className="text-sm text-gray-400 mb-2">Metrics</div>
                    <div className="bg-gray-800 p-3 rounded-lg">
                      <pre className="text-xs text-gray-300 overflow-x-auto">
                        {JSON.stringify(selectedModel.metrics, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setDetailsDialogOpen(false)}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteDialogOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-900 p-6 rounded-2xl w-full max-w-md shadow-2xl border border-gray-700">
            <h2 className="text-xl font-semibold text-white mb-4">
              {modelToDelete?.isProduction ? '⚠️ Delete Production Model' : 'Delete Model'}
            </h2>
            
            {modelToDelete?.isProduction && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
                <p className="text-red-400 text-sm font-medium">
                  <strong>WARNING:</strong> This is a PRODUCTION model! Deleting it may affect live services.
                </p>
              </div>
            )}
            
            <p className="text-white mb-2">
              Are you sure you want to delete model <strong className="text-red-400">{modelToDelete?.modelId}</strong>?
            </p>
            
            <p className="text-sm text-gray-400 mb-4">
              To confirm, type the model ID below:
            </p>
            
            <input
              type="text"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 mb-6"
              placeholder={modelToDelete?.modelId || ''}
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              autoFocus
            />
            
            <div className="flex justify-end gap-2">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteConfirmText !== modelToDelete?.modelId}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  deleteConfirmText === modelToDelete?.modelId
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                }`}
              >
                Delete Model
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelRegistrySimplified;
